import React, { useEffect, useState } from "react";
import { getEmployeeName } from "../../Config/Employees";
import {
  Box,
  Button,
  Badge,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import EmployeeDataService from "../../services/Employee";
import { getUserAccess } from "../../utils/authUtils";
import { HrmsTableFrame } from "../../components/Hrms/HrmsTable";
import { PersonCell } from "../../components/Hrms/hrmsUi";
import { HrmsPageShell } from "../../components/Hrms/HrmsPageShell";

export const EmployeeMonthlyConfirmation = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const userEmail = localStorage.getItem("logged_in_email") || "";
  const user = localStorage.getItem("logged_in_email") || "";
  const { isAdmin } = getUserAccess();
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [confirmations, setConfirmations] = useState<any[]>([]);
  const [currentMonthStatus, setCurrentMonthStatus] = useState<any>(null);

  // Submission form state
  const [leaveConfirmation, setLeaveConfirmation] = useState("confirmed");
  const [remarks, setRemarks] = useState("");
  const [reimbursementAmount, setReimbursementAmount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Admin filters
  const [filterMonth, setFilterMonth] = useState(DateTime.now().toFormat("MM"));
  const [filterYear, setFilterYear] = useState(DateTime.now().year.toString());
  const [filterStatus, setFilterStatus] = useState("");

  // Admin review modal
  const {
    isOpen: isReviewOpen,
    onOpen: onReviewOpen,
    onClose: onReviewClose,
  } = useDisclosure();
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [reviewStatus, setReviewStatus] = useState("");

  const currentMonth = DateTime.now().toFormat("MM");
  const currentYear = DateTime.now().year.toString();

  // Update status modal (admin)
  const {
    isOpen: isUpdateOpen,
    onOpen: onUpdateOpen,
    onClose: onUpdateClose,
  } = useDisclosure();
  const [updateData, setUpdateData] = useState({
    salaryStatus: "",
    salaryHoldReason: "",
  });

  // Add deduction modal (admin)
  const {
    isOpen: isDeductionOpen,
    onOpen: onDeductionOpen,
    onClose: onDeductionClose,
  } = useDisclosure();
  const [deduction, setDeduction] = useState({ reason: "", amount: "" });

  const generateMonthYearArray = () => {
    const startDate = DateTime.fromObject({ month: 11, year: 2025 });
    const endDate = DateTime.now();
    let currentDate = startDate;
    const arr: any[] = [];
    while (currentDate <= endDate) {
      arr.push({
        month: currentDate.toFormat("MM"),
        year: currentDate.year.toString(),
        text: `${currentDate.toFormat("MMMM")}-${currentDate.year}`,
      });
      currentDate = currentDate.plus({ months: 1 });
    }
    return arr;
  };

  const getStatusBadge = (status: string) => {
    const colorMap: any = {
      submitted: "blue",
      pending: "orange",
      correction_requested: "red",
      approved: "green",
    };
    return (
      <Badge colorScheme={colorMap[status] || "gray"}>
        {status?.replace(/_/g, " ")}
      </Badge>
    );
  };

  const getLeaveConfirmationBadge = (status: string) => {
    const colorMap: any = {
      confirmed: "green",
      correction_requested: "red",
    };
    return (
      <Badge colorScheme={colorMap[status] || "gray"}>
        {status?.replace(/_/g, " ") || "not submitted"}
      </Badge>
    );
  };

  const handleUpdateStatus = async () => {
    if (!selectedRecord) return;
    try {
      await EmployeeDataService.updateSalary(
        token,
        selectedRecord._id,
        {
          salaryStatus: updateData.salaryStatus,
          salaryHoldReason:
            updateData.salaryStatus === "on_hold"
              ? updateData.salaryHoldReason
              : "",
        },
        user,
      );
      toast({
        position: "top",
        title: "Salary status updated",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      onUpdateClose();
      fetchConfirmations();
    } catch (e: any) {
      toast({
        position: "top",
        title: "Update failed",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleAddDeduction = async () => {
    if (!selectedRecord || !deduction.reason || !deduction.amount) {
      toast({
        position: "top",
        title: "Please fill reason and amount",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    try {
      await EmployeeDataService.addSalaryDeduction(
        token,
        selectedRecord._id,
        { reason: deduction.reason, amount: Number(deduction.amount) },
        user,
      );
      toast({
        position: "top",
        title: "Deduction added",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      onDeductionClose();
      setDeduction({ reason: "", amount: "" });
      fetchConfirmations();
    } catch (e: any) {
      toast({
        position: "top",
        title: "Failed to add deduction",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const fetchConfirmations = async () => {
    setIsLoading(true);
    try {
      const res = await EmployeeDataService.getEmployeeMonthlyConfirmations(
        token,
        user,
        isAdmin ? "" : userEmail,
        isAdmin ? filterMonth : "",
        isAdmin ? filterYear : "",
      );
      if (res.data?.data) {
        setConfirmations(res.data.data);
        if (!isAdmin) {
          const current = res.data.data.find(
            (c: any) =>
              c.month === currentMonth && c.year?.toString() === currentYear,
          );
          setCurrentMonthStatus(current || null);
        }
      }
    } catch (e: any) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const data = {
        email: userEmail,
        name: getEmployeeName(userEmail),
        month: currentMonth,
        year: currentYear,
        leaveConfirmationStatus: leaveConfirmation,
        leaveRemarks:
          leaveConfirmation === "correction_requested" ? remarks : "",
        reimbursementAmount,
      };
      await EmployeeDataService.submitEmployeeMonthlyConfirmation(token, data);
      toast({
        position: "top",
        title: "Monthly confirmation submitted successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      setRemarks("");
      setReimbursementAmount(0);
      setLeaveConfirmation("confirmed");
      fetchConfirmations();
    } catch (e: any) {
      toast({
        position: "top",
        title: "Submission failed",
        description: e.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAdminUpdate = async () => {
    if (!selectedRecord) return;
    try {
      await EmployeeDataService.updateEmployeeMonthlyConfirmation(
        token,
        selectedRecord._id,
        { status: reviewStatus },
        user,
      );
      toast({
        position: "top",
        title: "Status updated successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      onReviewClose();
      fetchConfirmations();
    } catch (e: any) {
      toast({
        position: "top",
        title: "Update failed",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const getDeductionsTotal = (deductions: any[]) => {
    if (!deductions || !Array.isArray(deductions)) return 0;
    return deductions.reduce((sum: number, d: any) => sum + (d.amount || 0), 0);
  };

  useEffect(() => {
    fetchConfirmations();
  }, []);

  const filteredConfirmations = filterStatus
    ? confirmations.filter((c: any) => c.status === filterStatus)
    : confirmations;

  return (
    <HrmsPageShell
      title="Monthly Confirmation"
      description="Confirm your month's leave each month — salary is released only after confirmation"
      sectionKey="me"
      isLoading={isLoading}
    >
      <>
        {/* Warning Banner */}
        <Box
          bg="red.50"
          border="1px"
          borderColor="red.200"
          borderRadius="md"
          p={4}
          mb={6}
        >
          <Text color="red.600" fontWeight="semibold" m={0}>
            If Monthly Leave Confirmation is not submitted, salary will NOT be
            released.
          </Text>
        </Box>

        {/* Submission Form for employee (if not yet submitted) */}
        {!isAdmin && !currentMonthStatus && (
          <Box bg="white" borderWidth="1px" borderRadius="md" p={6} mb={6}>
            <Text fontWeight="bold" fontSize="md" mb={4}>
              Submit Confirmation for {DateTime.now().toFormat("MMMM yyyy")}
            </Text>

            <FormControl mb={4}>
              <FormLabel fontWeight="semibold">Leave Confirmation</FormLabel>
              <RadioGroup
                value={leaveConfirmation}
                onChange={setLeaveConfirmation}
              >
                <Stack direction="column" spacing={2}>
                  <Radio value="confirmed">Leaves recorded are accurate</Radio>
                  <Radio value="correction_requested">
                    Correction required
                  </Radio>
                </Stack>
              </RadioGroup>
            </FormControl>

            {leaveConfirmation === "correction_requested" && (
              <FormControl mb={4}>
                <FormLabel>Remarks</FormLabel>
                <Textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Describe the correction needed..."
                />
              </FormControl>
            )}

            <FormControl mb={4}>
              <FormLabel fontWeight="semibold">
                Reimbursement Amount (INR)
              </FormLabel>
              <Input
                type="number"
                value={reimbursementAmount}
                onChange={(e) => setReimbursementAmount(Number(e.target.value))}
                w="200px"
              />
              <Text fontSize="sm" color="gray.500" mt={1}>
                Note: Must send email with invoice for reimbursement claims.
              </Text>
            </FormControl>

            <Button
              bg="#327171"
              color="white"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              _hover={{ bg: "#285e5e" }}
            >
              Submit Confirmation
            </Button>
          </Box>
        )}

        {/* Filters */}
        <div className="top_section_container">
          <div className="top_section_lower_container">
            <div
              className="nested p-lg-0"
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <Flex p={4} alignItems="start" justifyContent="end" gap={8}>
                {isAdmin && (
                  <Flex display="flex" alignItems="flex-end" w="250px">
                    <Select
                      placeholder="All Statuses"
                      onChange={(e) => setFilterStatus(e.target.value)}
                      value={filterStatus}
                      color="#327171"
                      borderRightRadius={0}
                      focusBorderColor="teal"
                      borderWidth="0.2px"
                      _focus={{
                        borderWidth: "0.2px",
                        borderColor: "teal.200",
                      }}
                    >
                      <option value="submitted">Submitted</option>
                      <option value="pending">Pending</option>
                      <option value="correction_requested">
                        Correction Requested
                      </option>
                      <option value="approved">Approved</option>
                    </Select>
                    <Button
                      size="md"
                      borderRadius="md"
                      borderLeftRadius={0}
                      p={5}
                      colorScheme="whiteAlpha"
                      bg="#327171"
                      onClick={() => fetchConfirmations()}
                    >
                      Search
                    </Button>
                  </Flex>
                )}

                <Flex display="flex" alignItems="flex-end" w="250px">
                  <Select
                    placeholder="Select Month"
                    onChange={(e) => {
                      const selected = generateMonthYearArray().find(
                        (item) =>
                          `${item.month}-${item.year}` === e.target.value,
                      );
                      if (selected) {
                        setFilterMonth(selected.month);
                        setFilterYear(selected.year);
                      } else {
                        setFilterMonth("");
                        setFilterYear("");
                      }
                    }}
                    color="#327171"
                    borderRightRadius={0}
                    focusBorderColor="teal"
                    borderWidth="0.2px"
                    _focus={{
                      borderWidth: "0.2px",
                      borderColor: "teal.200",
                    }}
                  >
                    {generateMonthYearArray().map((el) => (
                      <option
                        key={`${el.month}-${el.year}`}
                        value={`${el.month}-${el.year}`}
                      >
                        {el.text}
                      </option>
                    ))}
                  </Select>
                  <Button
                    size="md"
                    borderRadius="md"
                    borderLeftRadius={0}
                    p={5}
                    colorScheme="whiteAlpha"
                    bg="#327171"
                    onClick={() => fetchConfirmations()}
                  >
                    Search
                  </Button>
                </Flex>
              </Flex>
            </div>
          </div>
        </div>

        {/* Summary Counts */}
        {!isAdmin && currentMonthStatus && (
          <Box my={8}>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={10}>
              <Box
                p={2}
                borderWidth="1px"
                borderRadius="lg"
                boxShadow="sm"
                textAlign="center"
                bg="white"
              >
                <Text
                  fontSize="sm"
                  color="gray.500"
                  fontWeight="semibold"
                  m={0}
                >
                  Current Month
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="teal.600" m={0}>
                  {DateTime.now().toFormat("MMMM yyyy")}
                </Text>
              </Box>
              <Box
                p={2}
                borderWidth="1px"
                borderRadius="lg"
                boxShadow="sm"
                textAlign="center"
                bg="white"
              >
                <Text
                  fontSize="sm"
                  color="gray.500"
                  fontWeight="semibold"
                  m={0}
                >
                  Leave Confirmation
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="teal.600" m={0}>
                  {getLeaveConfirmationBadge(
                    currentMonthStatus.leaveConfirmationStatus,
                  )}
                </Text>
              </Box>
              <Box
                p={2}
                borderWidth="1px"
                borderRadius="lg"
                boxShadow="sm"
                textAlign="center"
                bg="white"
              >
                <Text
                  fontSize="sm"
                  color="gray.500"
                  fontWeight="semibold"
                  m={0}
                >
                  Admin Status
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="teal.600" m={0}>
                  {getStatusBadge(currentMonthStatus.status || "pending")}
                </Text>
              </Box>
              <Box
                p={2}
                borderWidth="1px"
                borderRadius="lg"
                boxShadow="sm"
                textAlign="center"
                bg="white"
              >
                <Text
                  fontSize="sm"
                  color="gray.500"
                  fontWeight="semibold"
                  m={0}
                >
                  Reimbursement
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="teal.600" m={0}>
                  {currentMonthStatus.reimbursementAmount || 0}
                </Text>
              </Box>
            </SimpleGrid>
          </Box>
        )}

        {isAdmin && (
          <Box my={8}>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={10}>
              {["submitted", "pending", "correction_requested", "approved"].map(
                (s) => (
                  <Box
                    key={s}
                    p={2}
                    borderWidth="1px"
                    borderRadius="lg"
                    boxShadow="sm"
                    textAlign="center"
                    bg="white"
                  >
                    <Text
                      fontSize="sm"
                      color="gray.500"
                      fontWeight="semibold"
                      m={0}
                    >
                      {s.replace(/_/g, " ").toUpperCase()}
                    </Text>
                    <Text
                      fontSize="lg"
                      fontWeight="bold"
                      color="teal.600"
                      m={0}
                    >
                      {
                        confirmations.filter(
                          (c: any) =>
                            c.status === s || (!c.status && s === "pending"),
                        ).length
                      }
                    </Text>
                  </Box>
                ),
              )}
            </SimpleGrid>
          </Box>
        )}

        {/* Main section */}
        <HrmsTableFrame>
          <table className="table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Period</th>
                <th>Leave Confirmation</th>
                {!isAdmin && <th>Admin Status</th>}
                <th>Reimbursement</th>
                <th>Remarks</th>
                <th>Deductions</th>
                {!isAdmin && <th>Salary Status</th>}

                {isAdmin && <th>Action</th>}
                {isAdmin && <th>Salary Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredConfirmations.length > 0 ? (
                filteredConfirmations.map((c: any) => (
                  <tr key={c._id}>
                    <td>
                      <PersonCell name={c.name || c.email} subtitle={c.email} />
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {DateTime.fromObject({
                        month: parseInt(c.month),
                      }).toFormat("MMMM")}{" "}
                      {c.year}
                    </td>
                    <td>
                      {getLeaveConfirmationBadge(c.leaveConfirmationStatus)}
                    </td>
                    {!isAdmin && <td>{getStatusBadge(c.status || "pending")}</td>}
                    <td>{c.reimbursementAmount || 0}</td>
                    <td>{c.leaveRemarks || "-"}</td>
                    <td>{getDeductionsTotal(c.deductions)}</td>
                    {!isAdmin && (
                      <td>
                        {getStatusBadge(c.salaryStatus || "pending")}
                        {c.salaryStatus === "on_hold" && c.salaryHoldReason && (
                          <Text fontSize="xs" color="red.500" m={0}>
                            {c.salaryHoldReason}
                          </Text>
                        )}
                      </td>
                    )}

                    {isAdmin && (
                      <td>
                        <Button
                          size="xs"
                          colorScheme="teal"
                          onClick={() => {
                            setSelectedRecord(c);
                            setReviewStatus(c.status || "submitted");
                            onReviewOpen();
                          }}
                        >
                          Review
                        </Button>
                      </td>
                    )}
                    {isAdmin && (
                      <td>
                        <Flex gap={2}>
                          <Button
                            size="xs"
                            colorScheme="teal"
                            onClick={() => {
                              setSelectedRecord(c);
                              setUpdateData({
                                salaryStatus: c.salaryStatus || "pending",
                                salaryHoldReason: c.salaryHoldReason || "",
                              });
                              onUpdateOpen();
                            }}
                          >
                            Status
                          </Button>
                          <Button
                            size="xs"
                            colorScheme="orange"
                            variant="outline"
                            onClick={() => {
                              setSelectedRecord(c);
                              setDeduction({ reason: "", amount: "" });
                              onDeductionOpen();
                            }}
                          >
                            Deduction
                          </Button>
                        </Flex>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={isAdmin ? 9 : 8}
                    style={{ textAlign: "center", color: "#a0aec0" }}
                  >
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </HrmsTableFrame>

        {/* Review Modal (admin only) */}
        <Modal isOpen={isReviewOpen} onClose={onReviewClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Review Confirmation</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {selectedRecord && (
                <>
                  <Text mb={2}>
                    <strong>Employee:</strong>{" "}
                    {selectedRecord.name || selectedRecord.email}
                  </Text>
                  <Text mb={2}>
                    <strong>Leave Confirmation:</strong>{" "}
                    {selectedRecord.leaveConfirmationStatus}
                  </Text>
                  <Text mb={2}>
                    <strong>Remarks:</strong>{" "}
                    {selectedRecord.leaveRemarks || "None"}
                  </Text>
                  <Text mb={4}>
                    <strong>Reimbursement:</strong>{" "}
                    {selectedRecord.reimbursementAmount || 0}
                  </Text>
                  <FormControl>
                    <FormLabel>Update Status</FormLabel>
                    <Select
                      value={reviewStatus}
                      onChange={(e) => setReviewStatus(e.target.value)}
                    >
                      <option value="submitted">Submitted</option>
                      <option value="approved">Approved</option>
                      <option value="correction_requested">
                        Correction Requested
                      </option>
                    </Select>
                  </FormControl>
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <Button mr={3} onClick={onReviewClose}>
                Cancel
              </Button>
              <Button
                bg="#327171"
                color="white"
                onClick={handleAdminUpdate}
                _hover={{ bg: "#285e5e" }}
              >
                Update
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Update Status Modal */}
        <Modal isOpen={isUpdateOpen} onClose={onUpdateClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Update Salary Status</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {selectedRecord && (
                <>
                  <Text mb={3}>
                    <strong>
                      {selectedRecord.name || selectedRecord.email}
                    </strong>{" "}
                    —{" "}
                    {DateTime.fromObject({
                      month: parseInt(selectedRecord.month),
                    }).toFormat("MMMM")}{" "}
                    {selectedRecord.year}
                  </Text>
                  <FormControl mb={3}>
                    <FormLabel>Status</FormLabel>
                    <Select
                      value={updateData.salaryStatus}
                      onChange={(e) =>
                        setUpdateData({
                          ...updateData,
                          salaryStatus: e.target.value,
                        })
                      }
                    >
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="on_hold">On Hold</option>
                    </Select>
                  </FormControl>
                  {updateData.salaryStatus === "on_hold" && (
                    <FormControl mb={3}>
                      <FormLabel>Hold Reason</FormLabel>
                      <Textarea
                        value={updateData.salaryHoldReason}
                        onChange={(e) =>
                          setUpdateData({
                            ...updateData,
                            salaryHoldReason: e.target.value,
                          })
                        }
                        placeholder="Reason for holding salary"
                      />
                    </FormControl>
                  )}
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <Button mr={3} onClick={onUpdateClose}>
                Cancel
              </Button>
              <Button
                bg="#327171"
                color="white"
                onClick={handleUpdateStatus}
                _hover={{ bg: "#285e5e" }}
              >
                Update
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Add Deduction Modal */}
        <Modal isOpen={isDeductionOpen} onClose={onDeductionClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Add Deduction</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {selectedRecord && (
                <Text mb={3}>
                  <strong>{selectedRecord.name || selectedRecord.email}</strong>{" "}
                  —{" "}
                  {DateTime.fromObject({
                    month: parseInt(selectedRecord.month),
                  }).toFormat("MMMM")}{" "}
                  {selectedRecord.year}
                </Text>
              )}

              <FormControl mb={3} isRequired>
                <FormLabel>Amount</FormLabel>
                <Input
                  type="number"
                  value={deduction.amount}
                  onChange={(e) =>
                    setDeduction({ ...deduction, amount: e.target.value })
                  }
                  placeholder="Amount"
                />
              </FormControl>
              <FormControl mb={3} isRequired>
                <FormLabel>Reason</FormLabel>
                <Textarea
                  value={deduction.reason}
                  onChange={(e) =>
                    setDeduction({ ...deduction, reason: e.target.value })
                  }
                  placeholder="Deduction reason"
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button mr={3} onClick={onDeductionClose}>
                Cancel
              </Button>
              <Button
                bg="#327171"
                color="white"
                onClick={handleAddDeduction}
                _hover={{ bg: "#285e5e" }}
              >
                Add Deduction
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    </HrmsPageShell>
  );
};
